/**
 * Iter 221 — Health Services Command Centre (Wave 1 · Foundation)
 *
 * Superadmin sees ONE landing at /health-services with tabs for every
 * Wave-1 sub-module. Each tab is intentionally small — the goal is to
 * ship the plumbing (arm-tagged catalogue, providers slice, HSR pipeline,
 * InstaBook primitive) so subsequent waves plug in cleanly.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Heartbeat, ListChecks, Users, ShieldCheck, Lightning,
  Plus, MagnifyingGlass, ArrowClockwise, X, Bandaids,
  Stethoscope, Truck, CalendarCheck, CurrencyInr,
} from "@phosphor-icons/react";
import { HealthBookingsTab } from "./HealthBookingsTab";
import { PaymentCell, RecordPaymentDialog } from "../../components/payments/PaymentInfo";
import { ExportPaymentsButton } from "../../components/payments/ExportPayments";

const TABS = [
  { key: "overview",  label: "Overview",      icon: Heartbeat  },
  { key: "catalogue", label: "Service Catalogue", icon: ListChecks },
  { key: "partners",  label: "Providers",     icon: Stethoscope },
  { key: "hsr",       label: "Service Requests", icon: Bandaids },
  { key: "bookings",  label: "Health Bookings", icon: CalendarCheck },
  { key: "instabook", label: "InstaBook",     icon: Lightning },
  { key: "trust",     label: "Trust & Verification", icon: ShieldCheck },
];

const URGENCY_STYLE = {
  routine: "bg-[#E5E2D9] text-[#5C5A55]",
  priority: "bg-[#FEF3C7] text-[#B45309]",
  urgent: "bg-[#FED7AA] text-[#C2410C]",
  emergency: "bg-[#FEE2E2] text-[#B91C1C]",
};

const STATUS_STYLE = {
  new:               "bg-[#DBEAFE] text-[#1D4ED8]",
  triaged:           "bg-[#E0E7FF] text-[#4338CA]",
  provider_assigned: "bg-[#FEF3C7] text-[#92400E]",
  confirmed:         "bg-[#D1FAE5] text-[#065F46]",
  in_progress:       "bg-[#CFFAFE] text-[#0E7490]",
  completed:         "bg-[#D4E0D1] text-[#1A3317]",
  cancelled:         "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function HealthServicesCommand() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);

  const loadSummary = async () => {
    try {
      const r = await api.get("/health-services/summary");
      setSummary(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadSummary(); }, []);

  return (
    <AppShell
      title="Health Services"
      subtitle="Command Centre"
      actions={
        <button
          data-testid="health-refresh"
          onClick={loadSummary}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-[#E5E2D9] bg-white hover:bg-[#F7F6F2]"
        >
          <ArrowClockwise size={14}/> Refresh
        </button>
      }
    >
      <div className="space-y-6" data-testid="health-services-root">
        {/* Command centre summary tiles */}
        <SummaryStrip summary={summary}/>

        {/* Tab bar */}
        <div className="flex items-center gap-2 border-b border-[#E5E2D9] overflow-x-auto" role="tablist">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`health-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  active
                    ? "border-[#B91C1C] text-[#B91C1C]"
                    : "border-transparent text-[#5C5A55] hover:text-[#1A1A1A]"
                }`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"}/>
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab bodies */}
        {tab === "overview"  && <OverviewTab summary={summary}/>}
        {tab === "catalogue" && <CatalogueTab/>}
        {tab === "partners"  && <PartnersTab/>}
        {tab === "hsr"       && <HsrTab/>}
        {tab === "bookings"  && <HealthBookingsTab/>}
        {tab === "instabook" && <InstabookTab/>}
        {tab === "trust"     && <TrustTab/>}
      </div>
    </AppShell>
  );
}

// ================================================================
// Summary tiles
// ================================================================
function SummaryStrip({ summary }) {
  const cards = [
    { key: "services_active",  label: "Active services",   value: summary?.services_active ?? "—", color: "#B91C1C", bg: "#FEE2E2", icon: ListChecks },
    { key: "partners_health",  label: "Health providers",   value: summary?.partners_health ?? "—", color: "#0F766E", bg: "#CCFBF1", icon: Stethoscope },
    { key: "hsr_open",         label: "Open service requests", value: summary?.hsr_open ?? "—",  color: "#B45309", bg: "#FEF3C7", icon: Bandaids },
    { key: "hsr_total",        label: "Total HSRs (all-time)", value: summary?.hsr_total ?? "—", color: "#1D4ED8", bg: "#DBEAFE", icon: Heartbeat },
    { key: "trust_expiring",   label: "Trust docs expiring", value: summary?.trust_expiring ?? "—", color: "#7A2E18", bg: "#F4C7B9", icon: ShieldCheck },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="health-summary-strip">
      {cards.map(c => {
        const Icon = c.icon;
        return (
          <div key={c.key} className="p-4 bg-white rounded-lg border border-[#E5E2D9]"
                data-testid={`health-tile-${c.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: c.bg }}>
                <Icon size={16} color={c.color} weight="fill"/>
              </div>
            </div>
            <div className="text-[24px] font-heading font-semibold text-[#1A1A1A]">{c.value}</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ================================================================
// Overview
// ================================================================
function OverviewTab({ summary }) {
  const roadmap = [
    { wave: "Wave 1 · Now",   items: ["Arm-tagged Service Catalogue", "Providers Directory (Health slice)", "HSR pipeline · Admin console", "InstaBook priority primitive", "Trust & Verification vault", "Business-Line switcher"] },
    { wave: "Wave 2 · Next",  items: ["Customer-app OP appointment booking", "AI Symptom Triage router", "Pharmacy Rx auto-quote", "Home nursing / physio scheduling", "Ambulance dispatch centre", "Public ambulance portal + hierarchy tiers"] },
    { wave: "Wave 3 · Later", items: ["Insurance & TPA bridge", "ABDM / ABHA integration", "Provider payouts (Razorpay Route)", "Adverse-event register", "Reviews & NPS", "Corporate & Karunya scheme accounts"] },
    { wave: "Wave 4 · AirQ",  items: ["Fleet & operator registry", "Dronepad + helipad infrastructure", "Air Dispatch Centre", "Live Sky Map", "Insurer bridge & pre-auth", "Public / DGCA / SDMA tiers"] },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="health-overview">
      <div className="p-5 bg-white rounded-lg border border-[#E5E2D9]">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">Vision</div>
        <div className="font-heading text-xl text-[#1A1A1A] mb-2">One tap to the right care</div>
        <p className="text-sm text-[#5C5A55] leading-relaxed">
          Any citizen can book a doctor, lab, pharmacy, nurse or ambulance from the customer app. We route it to the right partner, we bill, we settle, we track quality — and surface the operational picture to the state health department and traffic police in real time.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-md bg-[#FEE2E2]">
            <div className="text-[10px] uppercase tracking-wider text-[#B91C1C] font-medium">Ground</div>
            <div className="text-xs text-[#5C5A55] mt-0.5">Ambulance + Home care</div>
          </div>
          <div className="p-2 rounded-md bg-[#DBEAFE]">
            <div className="text-[10px] uppercase tracking-wider text-[#1D4ED8] font-medium">OP</div>
            <div className="text-xs text-[#5C5A55] mt-0.5">Clinic + InstaBook</div>
          </div>
          <div className="p-2 rounded-md bg-[#E0E7FF]">
            <div className="text-[10px] uppercase tracking-wider text-[#4338CA] font-medium">Air</div>
            <div className="text-xs text-[#5C5A55] mt-0.5">AirQ · Wave 4</div>
          </div>
        </div>
      </div>
      <div className="p-5 bg-white rounded-lg border border-[#E5E2D9]">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">Delivery roadmap</div>
        <div className="space-y-3">
          {roadmap.map(r => (
            <div key={r.wave} className="border-l-2 border-[#B91C1C] pl-3">
              <div className="text-sm font-medium text-[#1A1A1A]">{r.wave}</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {r.items.map((it, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full text-[11px] bg-[#F5F3EE] text-[#5C5A55]">{it}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// Catalogue tab
// ================================================================
function CatalogueTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/health-services/catalogue?arm=health");
      setRows(r.data.services || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r =>
    !q || r.name.toLowerCase().includes(q.toLowerCase()) ||
          r.code.toLowerCase().includes(q.toLowerCase()) ||
          (r.category || "").toLowerCase().includes(q.toLowerCase())
  ), [rows, q]);

  const toggleActive = async (row) => {
    try {
      await api.patch(`/health-services/catalogue/${row.id}`, { active: !row.active });
      toast.success(`${row.name} ${row.active ? "hidden" : "enabled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="catalogue-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input
            data-testid="catalogue-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search services, code, or category…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-[#E5E2D9] focus:border-[#B91C1C] focus:outline-none"
          />
        </div>
        <button
          data-testid="catalogue-add"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-[#B91C1C] text-white hover:bg-[#991B1B]"
        >
          <Plus size={14}/> Add service
        </button>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Mode</th>
              <th className="text-right px-4 py-3">Base price</th>
              <th className="text-right px-4 py-3">Commission</th>
              <th className="text-center px-4 py-3">Licensed pro</th>
              <th className="text-center px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EDE4]">
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-[#8A8780]">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[#8A8780]">No services</td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.id} data-testid={`catalogue-row-${r.code}`} className="hover:bg-[#FAFAF7]">
                <td className="px-4 py-3">
                  <div className="font-medium text-[#1A1A1A]">{r.name}</div>
                  <div className="text-[11px] text-[#8A8780] font-mono">{r.code}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#F5F3EE] text-[#5C5A55]">{r.category}</span>
                  {r.subcategory && <span className="ml-1 text-[11px] text-[#8A8780]">· {r.subcategory}</span>}
                </td>
                <td className="px-4 py-3 text-[#5C5A55]">{r.mode}</td>
                <td className="px-4 py-3 text-right font-mono">₹{Number(r.base_price || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right text-[#5C5A55]">{r.commission_pct || 0}%</td>
                <td className="px-4 py-3 text-center">{r.needs_licensed_pro ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    data-testid={`catalogue-toggle-${r.code}`}
                    onClick={() => toggleActive(r)}
                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      r.active ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#E5E2D9] text-[#5C5A55]"
                    }`}
                  >
                    {r.active ? "Active" : "Hidden"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddCatalogueModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }}/>
      )}
    </div>
  );
}

function AddCatalogueModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    code: "", name: "", category: "consult", subcategory: "",
    base_price: 0, mode: "hybrid", needs_licensed_pro: false,
    commission_pct: 12, description: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.code || !form.name) { toast.error("Code and name required"); return; }
    try {
      setSaving(true);
      await api.post("/health-services/catalogue", { ...form, arm: "health" });
      toast.success("Service added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}
          data-testid="catalogue-add-modal">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">Add health service</h2>
          <button onClick={onClose} data-testid="catalogue-add-close" className="p-1 rounded hover:bg-[#F5F3EE]"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (unique)"><input data-testid="catalogue-input-code" value={form.code} onChange={e => set("code", e.target.value)} className="input"/></Field>
          <Field label="Name"><input data-testid="catalogue-input-name" value={form.name} onChange={e => set("name", e.target.value)} className="input"/></Field>
          <Field label="Category"><input value={form.category} onChange={e => set("category", e.target.value)} className="input"/></Field>
          <Field label="Subcategory"><input value={form.subcategory} onChange={e => set("subcategory", e.target.value)} className="input"/></Field>
          <Field label="Base price (₹)"><input type="number" value={form.base_price} onChange={e => set("base_price", Number(e.target.value))} className="input"/></Field>
          <Field label="Commission %"><input type="number" value={form.commission_pct} onChange={e => set("commission_pct", Number(e.target.value))} className="input"/></Field>
          <Field label="Mode">
            <select value={form.mode} onChange={e => set("mode", e.target.value)} className="input">
              <option value="clinic">Clinic</option>
              <option value="home">Home</option>
              <option value="delivery">Delivery</option>
              <option value="on_call">On-call</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </Field>
          <Field label="Needs licensed professional">
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={form.needs_licensed_pro} onChange={e => set("needs_licensed_pro", e.target.checked)}/>
              Yes
            </label>
          </Field>
          <div className="col-span-2">
            <Field label="Description">
              <textarea value={form.description} onChange={e => set("description", e.target.value)} className="input" rows={2}/>
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-[#E5E2D9]">Cancel</button>
          <button data-testid="catalogue-save" disabled={saving} onClick={submit} className="px-3 py-1.5 text-sm rounded-md bg-[#B91C1C] text-white disabled:opacity-50">{saving ? "Saving…" : "Add service"}</button>
        </div>
        <style>{`.input{width:100%;border:1px solid #E5E2D9;border-radius:6px;padding:8px 10px;font-size:14px}.input:focus{outline:none;border-color:#B91C1C}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mb-1">{label}</div>
      {children}
    </label>
  );
}

// ================================================================
// Partners tab
// ================================================================
function PartnersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(); if (q) params.set("q", q);
      const r = await api.get(`/health-services/partners?${params}`);
      setRows(r.data.partners || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="partners-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input
            data-testid="partners-search"
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Search partner name / email…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-[#E5E2D9]"
          />
        </div>
        <a href="/admin/service-partners" className="text-sm text-[#B91C1C] hover:underline">Manage → Service Partners</a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading && <div className="text-[#8A8780] text-sm">Loading…</div>}
        {!loading && rows.length === 0 && <div className="text-[#8A8780] text-sm">No health providers yet</div>}
        {rows.map(p => (
          <div key={p.id} data-testid={`partner-card-${p.id}`}
                className="p-4 bg-white rounded-lg border border-[#E5E2D9] hover:border-[#B91C1C] transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 rounded-md bg-[#FEE2E2] flex items-center justify-center">
                <Stethoscope size={18} color="#B91C1C" weight="fill"/>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] ${p.is_active === false ? "bg-[#E5E2D9] text-[#5C5A55]" : "bg-[#D4E0D1] text-[#1A3317]"}`}>
                {p.is_active === false ? "Inactive" : "Active"}
              </span>
            </div>
            <div className="font-medium text-[#1A1A1A]">{p.name}</div>
            <div className="text-[11px] text-[#8A8780] mt-0.5">{p.subcategory} · {p.service_mode}</div>
            <div className="text-sm text-[#5C5A55] mt-2 truncate">{p.email}</div>
            <div className="text-[11px] text-[#8A8780]">{p.phone}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {(p.catalogue_bindings || []).slice(0, 3).map((b, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-[#F5F3EE]">{b.code}</span>
              ))}
              {(!p.catalogue_bindings || p.catalogue_bindings.length === 0) && (
                <span className="text-[10px] text-[#8A8780]">No services mapped yet</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ================================================================
// HSR tab
// ================================================================
function HsrTab() {
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [flow, setFlow] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (q) params.set("q", q);
      const r = await api.get(`/health-services/hsr?${params}`);
      setRows(r.data.hsrs || []);
      setBuckets(r.data.by_status || {});
      setFlow(r.data.flow || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */}, [status]);

  return (
    <div className="space-y-4" data-testid="hsr-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="hsr-status-all"
          onClick={() => setStatus("all")}
          className={`px-3 py-1.5 text-sm rounded-md ${status === "all" ? "bg-[#B91C1C] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55]"}`}>
          All
        </button>
        {flow.map(s => (
          <button
            key={s}
            data-testid={`hsr-status-${s}`}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-sm rounded-md ${status === s ? "bg-[#B91C1C] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55]"}`}>
            {s.replace(/_/g, " ")} <span className="ml-1 opacity-70">{buckets[s] || 0}</span>
          </button>
        ))}
        <div className="flex-1"/>
        <ExportPaymentsButton url="/health-services/hsr/export.csv" testid="hsr-export"/>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input
            data-testid="hsr-search"
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Search…"
            className="pl-9 pr-3 py-1.5 text-sm rounded-md border border-[#E5E2D9]"
          />
        </div>
        <button
          data-testid="hsr-new"
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#B91C1C] text-white hover:bg-[#991B1B]">
          <Plus size={14}/> New HSR
        </button>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">HSR#</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-center px-4 py-3">Urgency</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EDE4]">
            {loading && <tr><td colSpan={8} className="text-center py-8 text-[#8A8780]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10">
                <div className="text-[#8A8780]">No service requests yet</div>
                <button onClick={() => setShowNew(true)} className="mt-3 text-sm text-[#B91C1C] hover:underline">Create the first one →</button>
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} data-testid={`hsr-row-${r.hsr_no}`} className="hover:bg-[#FAFAF7]">
                <td className="px-4 py-3 font-mono text-[12px]">{r.hsr_no}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="text-[11px] text-[#8A8780]">{r.customer_phone}</div>
                </td>
                <td className="px-4 py-3">
                  {r.service_name}
                  {r.is_instabook && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] bg-[#FEF3C7] text-[#B45309]">⚡ InstaBook</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] ${URGENCY_STYLE[r.urgency] || ""}`}>{r.urgency}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] ${STATUS_STYLE[r.status] || ""}`}>{r.status.replace(/_/g, " ")}</span>
                </td>
                <td className="px-4 py-3"><PaymentCell payment={r.payment_summary} testid={`hsr-payment-${r.hsr_no}`}/></td>
                <td className="px-4 py-3 text-[11px] text-[#8A8780]">{fmtRelative(r.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  {r.payment_summary?.state === "unpaid" && r.status !== "cancelled" && (
                    <button onClick={() => setPayFor(r)} data-testid={`hsr-record-pay-${r.hsr_no}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#B91C1C] hover:underline whitespace-nowrap">
                      <CurrencyInr size={12}/>Record payment
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payFor && (
        <RecordPaymentDialog url={`/health-services/hsr/${payFor.id}/payment`} title={payFor.hsr_no}
                             onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }}/>
      )}
      {showNew && <NewHsrModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
    </div>
  );
}

function NewHsrModal({ onClose, onSaved }) {
  const [catalogue, setCatalogue] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", service_code: "",
    urgency: "routine", address: "", notes: "",
    is_instabook: false, instabook_tier: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/health-services/catalogue?arm=health&active=true")
      .then(r => setCatalogue(r.data.services || []))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.customer_name || !form.customer_phone || !form.service_code) {
      toast.error("Customer name, phone, and service are required"); return;
    }
    try {
      setSaving(true);
      await api.post("/health-services/hsr", form);
      toast.success("HSR created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}
          data-testid="hsr-new-modal">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">New Health Service Request</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F5F3EE]"><X size={16}/></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer name"><input data-testid="hsr-input-name" value={form.customer_name} onChange={e => set("customer_name", e.target.value)} className="input"/></Field>
            <Field label="Phone"><input data-testid="hsr-input-phone" value={form.customer_phone} onChange={e => set("customer_phone", e.target.value)} className="input"/></Field>
          </div>
          <Field label="Service">
            <select data-testid="hsr-input-service" value={form.service_code} onChange={e => set("service_code", e.target.value)} className="input">
              <option value="">Select a service…</option>
              {catalogue.map(s => <option key={s.code} value={s.code}>{s.name} — ₹{s.base_price}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Urgency">
              <select value={form.urgency} onChange={e => set("urgency", e.target.value)} className="input">
                <option value="routine">Routine</option>
                <option value="priority">Priority</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </Field>
            <Field label="InstaBook">
              <label className="flex items-center gap-2 text-sm mt-2">
                <input type="checkbox" checked={form.is_instabook} onChange={e => set("is_instabook", e.target.checked)}/>
                Priority appointment
              </label>
            </Field>
          </div>
          {form.is_instabook && (
            <Field label="InstaBook tier">
              <select value={form.instabook_tier} onChange={e => set("instabook_tier", e.target.value)} className="input">
                <option value="">Pick tier…</option>
                <option value="swift">Swift · ≤ 30 min · ₹99</option>
                <option value="priority">Priority · ≤ 20 min · ₹149</option>
                <option value="urgent">Urgent · ≤ 10 min · ₹249</option>
              </select>
            </Field>
          )}
          <Field label="Address"><input value={form.address} onChange={e => set("address", e.target.value)} className="input"/></Field>
          <Field label="Notes"><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} className="input"/></Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-[#E5E2D9]">Cancel</button>
          <button data-testid="hsr-save" disabled={saving} onClick={submit} className="px-3 py-1.5 text-sm rounded-md bg-[#B91C1C] text-white disabled:opacity-50">{saving ? "Saving…" : "Create HSR"}</button>
        </div>
        <style>{`.input{width:100%;border:1px solid #E5E2D9;border-radius:6px;padding:8px 10px;font-size:14px}.input:focus{outline:none;border-color:#B91C1C}`}</style>
      </div>
    </div>
  );
}

// ================================================================
// InstaBook tab
// ================================================================
function InstabookTab() {
  const [tiers, setTiers] = useState([]);
  const [buckets, setBuckets] = useState([]);
  useEffect(() => {
    api.get("/health-services/instabook/tiers").then(r => setTiers(r.data.tiers || []));
    api.get("/health-services/instabook/fairness").then(r => setBuckets(r.data.buckets || []));
  }, []);
  return (
    <div className="space-y-4" data-testid="instabook-tab">
      <div className="p-4 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] flex gap-3">
        <Lightning size={22} color="#B45309" weight="fill"/>
        <div className="text-sm text-[#7A5A11]">
          <div className="font-medium mb-1">InstaBook · Priority appointment primitive</div>
          <p>Charges a priority fee up-front, guarantees a slot within a time cap, refunds automatically if we can't fit. Arm-tagged so the same primitive works for hospital OP today, birthday-cake same-day rush tomorrow.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">Priority-fee tiers</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {tiers.map(t => (
            <div key={t.code} data-testid={`instabook-tier-${t.code}`} className="p-4 rounded-md border border-[#E5E2D9]">
              <div className="text-xs uppercase tracking-wider text-[#8A8780]">{t.code}</div>
              <div className="font-heading text-2xl text-[#1A1A1A] mt-1">₹{t.fee}</div>
              <div className="text-xs text-[#5C5A55] mt-1">{t.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Fairness dashboard</div>
          <span className="text-[11px] text-[#8A8780]">Priority vs Regular booking mix per provider · daily cap watchdog</span>
        </div>
        {buckets.length === 0 ? (
          <div className="text-sm text-[#8A8780] py-8 text-center">No InstaBook bookings yet — this dashboard lights up once bookings start flowing.</div>
        ) : (
          <div className="text-sm text-[#5C5A55]">{buckets.length} buckets tracked. Details table in wave 2.</div>
        )}
      </div>
    </div>
  );
}

// ================================================================
// Trust & Verification tab
// ================================================================
function TrustTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/health-services/trust");
      setRows(r.data.docs || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="trust-tab">
      <div className="p-4 rounded-lg bg-[#E0E7FF] border border-[#C7D2FE] flex gap-3">
        <ShieldCheck size={22} color="#4338CA" weight="fill"/>
        <div className="text-sm text-[#312E81]">
          <div className="font-medium mb-1">Trust & Verification vault</div>
          <p>Shared vault for medical council licences, NABH/NABL, drug licences, ambulance permits — and, tomorrow, Happiness sitter police clearances and Hygiene worker IDs. Expiry alerts fire in the topbar Notification Bell.</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">{rows.length} document(s)</div>
        <button
          data-testid="trust-add"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#4338CA] text-white hover:bg-[#3730A3]">
          <Plus size={14}/> Add trust document
        </button>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Document</th>
              <th className="text-left px-4 py-3">Doc no.</th>
              <th className="text-left px-4 py-3">Expires</th>
              <th className="text-center px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EDE4]">
            {loading && <tr><td colSpan={5} className="text-center py-8 text-[#8A8780]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-[#8A8780]">No trust documents yet</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} data-testid={`trust-row-${r.id}`} className="hover:bg-[#FAFAF7]">
                <td className="px-4 py-3">
                  <div className="font-medium">{r.subject_name}</div>
                  <div className="text-[11px] text-[#8A8780]">{r.subject_type}</div>
                </td>
                <td className="px-4 py-3">{r.doc_type}</td>
                <td className="px-4 py-3 font-mono text-[12px]">{r.doc_no || "—"}</td>
                <td className="px-4 py-3">{r.expires_on ? fmtDate(r.expires_on) : "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#D4E0D1] text-[#1A3317]">{r.status || "active"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddTrustModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}

function AddTrustModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    subject_type: "partner", subject_id: "", subject_name: "",
    doc_type: "medical_council", doc_no: "", file_url: "",
    issued_on: "", expires_on: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.subject_id || !form.subject_name || !form.doc_type) { toast.error("Fill subject and doc type"); return; }
    try {
      setSaving(true);
      await api.post("/health-services/trust", form);
      toast.success("Trust document added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}
          data-testid="trust-add-modal">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">Add trust document</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F5F3EE]"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject type">
            <select value={form.subject_type} onChange={e => set("subject_type", e.target.value)} className="input">
              <option value="partner">Partner</option>
              <option value="staff">Staff</option>
              <option value="sitter">Sitter</option>
              <option value="worker">Worker</option>
            </select>
          </Field>
          <Field label="Subject ID"><input value={form.subject_id} onChange={e => set("subject_id", e.target.value)} className="input"/></Field>
          <div className="col-span-2"><Field label="Subject name"><input value={form.subject_name} onChange={e => set("subject_name", e.target.value)} className="input"/></Field></div>
          <Field label="Doc type"><input value={form.doc_type} onChange={e => set("doc_type", e.target.value)} className="input" placeholder="medical_council / nabh / drug_licence…"/></Field>
          <Field label="Doc number"><input value={form.doc_no} onChange={e => set("doc_no", e.target.value)} className="input"/></Field>
          <Field label="Issued on"><input type="date" value={form.issued_on} onChange={e => set("issued_on", e.target.value)} className="input"/></Field>
          <Field label="Expires on"><input type="date" value={form.expires_on} onChange={e => set("expires_on", e.target.value)} className="input"/></Field>
          <div className="col-span-2"><Field label="File URL (optional)"><input value={form.file_url} onChange={e => set("file_url", e.target.value)} className="input"/></Field></div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-[#E5E2D9]">Cancel</button>
          <button data-testid="trust-save" disabled={saving} onClick={submit} className="px-3 py-1.5 text-sm rounded-md bg-[#4338CA] text-white disabled:opacity-50">{saving ? "Saving…" : "Add"}</button>
        </div>
        <style>{`.input{width:100%;border:1px solid #E5E2D9;border-radius:6px;padding:8px 10px;font-size:14px}.input:focus{outline:none;border-color:#4338CA}`}</style>
      </div>
    </div>
  );
}

// ================================================================
// helpers
// ================================================================
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const pad = n => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  } catch { return "—"; }
}
function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 60000; // minutes
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.floor(diff)}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return fmtDate(iso);
}
